<script lang="ts">
  /**
   * This component can be used to create Modals with. It is used like so:
   *
   * import Modal from '../Components/Modal.svelte';
   * let modal
   *
   * <button on:click={modal.show}>Open</button>
   *
   * <Modal bind:this={modal}>
   *   <p>My interesting content</p>
   * </Modal>
   */
  import { fly, fade } from "svelte/transition"
  import { isMobile } from "../lib/Helpers"
  import CloseIcon from "../Icons/Close.svg?raw"
  import Svg from "../components/Svg.svelte"
  import { getContext } from "svelte"
  import type { Theme } from "website_script/types/theme.ts"

  // Whether to close when the user clicks outside the modal
  export let closeOnClick = false
  export let customTheme: Partial<Theme> | null

  const theme = getContext<Theme>("theme")

  $: ({
    neutralTextColor,
    modalBackgroundColor,
    containerMaxWidth,
    modalPadding,
  } = {
    ...theme,
    ...customTheme,
  })

  $: wrapperStyles = [
    `--modal-bg-color: ${modalBackgroundColor}`,
    `--modal-max-width: ${containerMaxWidth}`,
    `--modal-padding: ${modalPadding}`,
  ].join(";")

  let contentEl: HTMLElement
  let isOpen: boolean = false
  let originalBodyOverflow: string
  let originalHtmlOverflow: string

  function lockScroll() {
    originalBodyOverflow = document.body.style.overflow
    originalHtmlOverflow = document.documentElement.style.overflow
    document.body.style.overflow = document.documentElement.style.overflow =
      "hidden"
  }

  function unlockScroll() {
    document.body.style.overflow = originalBodyOverflow
    document.documentElement.style.overflow = originalHtmlOverflow
  }

  /** function to show the module from another Component*/
  export const show = () => {
    if (isOpen) {
      console.warn("Modal is already open!")
      return
    }

    lockScroll()
    isOpen = true
  }

  export const scrollToTop = () => {
    contentEl.scroll(0, 0)
  }

  export const close = () => {
    if (!isOpen) {
      console.warn("Modal is already closed!")
      return
    }

    isOpen = false
    unlockScroll()
  }

  /** Closes the modal if you click on the overlay */
  const handleOuterClick = (event: Event) => {
    if (!closeOnClick) return

    event.preventDefault()
    close()
  }
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    class="auws-overlay"
    out:fade|global={{ duration: 250 }}
    on:click={handleOuterClick}
    on:keypress={() => {}}
    role="dialog"
    aria-modal="true"
  >
    <div
      class="auws-window-wrapper"
      class:auws-mobile={isMobile()}
      in:fly|global={{ y: 200, duration: 400 }}
    >
      <div class="auws-content-wrapper" style={wrapperStyles}>
        <div class="auws-content-header">
          <button class="auws-close-button" on:click={close}>
            <Svg
              data={CloseIcon}
              alt="close button"
              color={neutralTextColor}
              size="20px"
            />
          </button>
        </div>
        <div class="auws-content-body">
          <div class="auws-content" bind:this={contentEl}>
            <slot />
          </div>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .auws-overlay {
    position: fixed;
    z-index: 2147483647; /* max value */
    top: 0;
    left: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.66);
  }

  .auws-window-wrapper {
    position: relative;
    margin: 2rem;
    max-height: 100%;
  }

  .auws-window-wrapper.auws-mobile {
    margin: 0;
    height: 100%;
  }

  .auws-content-wrapper {
    position: relative;
    width: auto;
    width: 100%;
    max-width: var(--modal-max-width);
    max-height: 100%;
    margin: 2rem auto;
    color: black;
    border-radius: 0.5rem;
    background-color: var(--modal-bg-color);
  }

  .auws-window-wrapper.auws-mobile .auws-content-wrapper {
    width: 100%;
    margin: 0;
    height: 100%;
  }

  .auws-content {
    position: relative;
    padding: var(--modal-padding, 1rem 1rem 1.5rem 1rem);
    max-height: calc(100vh - 4rem);
    min-height: 4em;
    overflow: auto;
  }

  .auws-window-wrapper.auws-mobile .auws-content {
    max-height: 100vh;
  }

  .auws-content-header {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 1100;
    cursor: pointer;
  }

  .auws-close-button {
    -webkit-appearance: button;
    appearance: button;
    background-color: transparent;
    font-family: inherit;
    font-size: 100%;
    line-height: 0;
    border: 0;
    margin: 0;
    padding: 10px;
  }
</style>
